import { LegalLayout, Section } from "@/pages/legal/LegalLayout";
import { COMPANY } from "@/lib/site-data";

const Terms = () => {
  return (
    <LegalLayout
      testid="page-terms"
      title="Terms of Service"
      updated="December 2025"
      lead="These terms govern the relationship between Dream Technology sp. z o.o. and any person or entity that visits dreamaxis.net or engages our advisory, business-management and programme-management services."
    >
      <Section number="01" title="Who we are">
        <p>
          These Terms of Service (the “<strong>Terms</strong>”) are issued by{" "}
          <strong>{COMPANY.legalName}</strong>, a limited liability company
          incorporated under the laws of the Republic of Poland, with its
          registered office at {COMPANY.hqAddress}, entered in the Register of
          Entrepreneurs of the National Court Register (KRS), NIP / REGON{" "}
          <span className="font-mono">{COMPANY.regon}</span> (“<strong>DreamAxis</strong>”,
          “<strong>we</strong>”, “<strong>us</strong>”).
        </p>
        <p>
          By accessing this website ({COMPANY.domain}) or requesting our
          services, you (“<strong>Client</strong>”, “<strong>you</strong>”)
          agree to be bound by these Terms. If you do not accept them, please
          discontinue use of the website and do not submit any inquiry.
        </p>
      </Section>

      <Section number="02" title="Scope of services">
        <p>
          DreamAxis provides professional advisory services, including business
          management, project and programme management, regulatory and
          compliance advisory, technology delivery leadership and grant /
          funding support, principally across information technology,
          sustainability (including water infrastructure) and financial
          services sectors.
        </p>
        <p>
          The specific scope, deliverables, timeline, fees and commercial
          terms of any engagement will always be documented in a separate
          written agreement — typically a Master Services Agreement (MSA) and
          Statement of Work (SOW) — executed by both parties. Nothing on the
          website constitutes a binding offer under Article 66 of the Polish
          Civil Code.
        </p>
      </Section>

      <Section number="03" title="Use of the website">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>use the website in a manner that violates applicable Polish, EU or international law;</li>
          <li>attempt to gain unauthorised access to the website, its servers or connected systems;</li>
          <li>submit content that is unlawful, defamatory, misleading or infringes third-party rights;</li>
          <li>use the website to distribute malware, spam or automated scraping tools without written consent.</li>
        </ul>
        <p>
          We reserve the right to suspend access to the website or to
          individual features at any time, without prior notice, where
          reasonably required to protect users, our systems or our clients.
        </p>
      </Section>

      <Section number="04" title="Intellectual property">
        <p>
          The website, its content, code, design system, trademarks,
          methodologies, case studies and deliverables produced during an
          engagement remain the exclusive property of{" "}
          {COMPANY.legalName}, save for such rights as are expressly assigned
          or licensed to a Client under a signed engagement contract.
        </p>
        <p>
          You may not copy, reproduce, republish, translate or create
          derivative works from any content on {COMPANY.domain} without our
          prior written consent, except for personal, non-commercial reference.
        </p>
      </Section>

      <Section number="05" title="Confidentiality">
        <p>
          Any information exchanged during pre-engagement discussions or in the
          course of an engagement is treated as confidential. We are prepared
          to sign a mutual non-disclosure agreement (NDA) on request, in
          advance of any substantive discussion.
        </p>
      </Section>

      <Section number="06" title="Warranties & professional standards">
        <p>
          Services are performed with the professional diligence expected of
          an experienced management consultancy and in accordance with the
          standard of care set out in Article 355 § 2 of the Polish Civil
          Code. Unless expressly stated in a signed SOW, we do not warrant any
          specific financial, operational or regulatory outcome.
        </p>
        <p>
          DreamAxis does not provide legal advice, licensed investment advice,
          audit opinions or regulated financial services. Where such services
          are required, Clients should engage suitably licensed professionals.
        </p>
      </Section>

      <Section number="07" title="Limitation of liability">
        <p>
          To the maximum extent permitted by Polish law, our aggregate
          liability arising out of or in connection with an engagement is
          limited to the fees actually paid by the Client under the specific
          SOW to which the claim relates, in the twelve (12) months preceding
          the event giving rise to the claim.
        </p>
        <p>
          We are not liable for indirect, incidental, consequential or
          reputational damages, loss of profits, savings, data or opportunity.
          Nothing in these Terms excludes liability that cannot lawfully be
          excluded, including liability for damage caused intentionally (Art. 473 § 2 KC).
        </p>
      </Section>

      <Section number="08" title="Force majeure">
        <p>
          Neither party is liable for delay or failure to perform its
          obligations to the extent caused by events beyond its reasonable
          control, including acts of state authorities, armed conflict,
          embargoes, pandemics, natural disasters, extended failures of
          third-party telecommunications or energy providers.
        </p>
      </Section>

      <Section number="09" title="Governing law & jurisdiction">
        <p>
          These Terms are governed by the laws of the Republic of Poland. Any
          dispute arising from or in connection with the website or our
          services shall be finally resolved by the common courts competent
          for the registered office of {COMPANY.legalName} in Warsaw, unless
          the parties agree otherwise in writing.
        </p>
      </Section>

      <Section number="10" title="Changes to these Terms">
        <p>
          We may update these Terms from time to time. The current version is
          always published at {COMPANY.domain}/legal/terms with the “last
          updated” date shown above. Continued use of the website after an
          update constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section number="11" title="Contact">
        <p>
          Questions about these Terms may be sent to{" "}
          <a
            href={`mailto:${COMPANY.email}`}
            className="text-emerald-800 underline underline-offset-2"
          >
            {COMPANY.email}
          </a>{" "}
          or by post to {COMPANY.legalName}, {COMPANY.hqAddress}.
        </p>
      </Section>
    </LegalLayout>
  );
};

export default Terms;
